/*  ========================================================================
    # Components - EventEntry
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useLocalization from '../../hooks/useLocalization';
import useHistoryValues from '../../hooks/useHistoryValues';
import useGetParams from '../../hooks/useGetParams';
import bem from '../../utils/bem';
import useEventStore from '../../stores/events';
import useTranslation from '../../hooks/useTranslation';

const EventEntry = ({
  eventId,
  type,
  isOnline,
  location,
  title,
  dates,
  initalOpen,
  people,
  isClosable = true
}) => {
  const local = useLocalization();
  const __ = useTranslation();
  const [isOpen, setIsOpen] = useState(initalOpen);
  const { selectedDates, setSelectedDates, addDate, removeDate, getSelectedDates } =
    useEventStore();
  const getParams = useGetParams();

  // Icons
  const iconGlobe = `${local('svgSprite')}#globe`;
  const iconUserGroup = `${local('svgSprite')}#user-group`;
  const iconLocation = `${local('svgSprite')}#location`;
  const iconAngleDown = `${local('svgSprite')}#angle-down`;
  const iconCheckmark = `${local('svgSprite')}#checkmark`;

  const formatDate = date => {
    const dateObj = new Date(date);
    const dateString = dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return dateString;
  };

  // React to history changes
  const handleHistoryPop = values => {
    setSelectedDates(values.selectedDates ? values.selectedDates.split(',') : []);
  };

  const pushNewValues = useHistoryValues(
    {
      selectedDates
    },
    handleHistoryPop
  );

  const handleDateSelection = date => {
    // Remove filter or add filter
    if (selectedDates && selectedDates.find(el => el === date)) {
      removeDate(date);
    } else {
      addDate(date);
    }
  };

  const isActiveDate = date => {
    if (selectedDates) {
      return selectedDates.find(elm => elm === date);
    } else {
      return false;
    }
  };

  useEffect(() => {
    pushNewValues({ selectedDates });
    if (selectedDates.length > 0) {
      document
        .querySelectorAll('.s-event-submission__wrapper')[0]
        .classList.add('s-event-submission__wrapper--form');
    } else {
      document
        .querySelectorAll('.s-event-submission__wrapper')[0]
        .classList.remove('s-event-submission__wrapper--form');
    }
  }, [selectedDates]);

  useEffect(() => {
    const currentSelectedDates = getParams['selectedDates']
      ? getParams['selectedDates'].split(',')
      : null;
    if (currentSelectedDates) {
      currentSelectedDates.forEach(date => {
        addDate(date);
      });
    }
  }, []);

  return (
    <div className={bem('s-event-submission', 'event', { active: isOpen })}>
      <div className='s-event-submission__event-meta'>
        <span className='s-event-submission__event-type'>{type}</span>
        <span className='s-event-submission__event-location'>
          <svg viewBox='0 0 24 24'>
            {isOnline ? <use href={iconGlobe} /> : <use href={iconLocation} />}
          </svg>
          {location}
        </span>
        {people ? (
          <span className='s-event-submission__event-people'>
            <svg viewBox='0 0 24 24'>
              <use href={iconUserGroup} />
            </svg>
            {people}
          </span>
        ) : null}
        {isClosable && (
          <button className='c-button c-button--icon' onClick={() => setIsOpen(!isOpen)}>
            <svg viewBox='0 0 24 24'>
              <use href={iconAngleDown} />
            </svg>
          </button>
        )}
      </div>
      <p className='h4' dangerouslySetInnerHTML={{ __html: title }}></p>
      <div className='s-event-submission__event-dates'>
        <div className='s-event-submission__event-dates-wrapper'>
          {dates.length > 0 ? (
            <>
              {dates.map((date, index) => (
                <button
                  className={bem('s-event-submission', 'event-date', {
                    active: isActiveDate(`${eventId}-${date['date']}`)
                  })}
                  key={index}
                  onClick={() => handleDateSelection(`${eventId}-${date['date']}`)}
                >
                  <div className='s-event-submission__event-date-wrapper'>
                    <p className='s-event-submission__event-day'>{formatDate(date['date'])}</p>
                    {date['start_time'] ? (
                      <p className='s-event-submission__event-time'>
                        {date['start_time']}
                        {date['end_time'] ? ' - ' + date['end_time'] : null}
                      </p>
                    ) : null}
                  </div>
                  <span className='s-event-submission__checkbox'>
                    <svg viewBox='0 0 24 24'>
                      <use href={iconCheckmark} />
                    </svg>
                  </span>
                </button>
              ))}
            </>
          ) : (
            <p className='s-event-submission__status'>{__('submissions-missing-dates')} </p>
          )}
        </div>
      </div>
    </div>
  );
};

EventEntry.propTypes = {
  eventId: PropTypes.number,
  type: PropTypes.string,
  isOnline: PropTypes.bool,
  location: PropTypes.string,
  title: PropTypes.string,
  dates: PropTypes.array,
  initalOpen: PropTypes.bool,
  isClosable: PropTypes.bool,
  people: PropTypes.string
};

export default EventEntry;
